console.log('🚀 GM Conversation Analytics Store: Initializing conversation analytics module...');

const conversationAnalytics = {
  namespaced: true,

  state: {
    conversationData: {},
    volumeData: {},
    channelData: {},
    peakHoursData: {},
    isLoading: false,
    error: null
  },

  mutations: {
    SET_CONVERSATION_DATA(state, data) {
      console.log('📊 GM Conversation Analytics: Setting conversation data:', data);
      state.conversationData = data;
    },

    SET_VOLUME_DATA(state, data) {
      console.log('📊 GM Conversation Analytics: Setting volume data:', data);
      state.volumeData = data;
    },

    SET_CHANNEL_DATA(state, data) {
      console.log('📊 GM Conversation Analytics: Setting channel data:', data);
      state.channelData = data;
    },

    SET_PEAK_HOURS_DATA(state, data) {
      console.log('📊 GM Conversation Analytics: Setting peak hours data:', data);
      state.peakHoursData = data;
    },

    SET_LOADING(state, isLoading) {
      console.log('🔄 GM Conversation Analytics: Setting loading state:', isLoading);
      state.isLoading = isLoading;
    },

    SET_ERROR(state, error) {
      console.log('❌ GM Conversation Analytics: Setting error:', error);
      state.error = error;
    },

    CLEAR_ERROR(state) {
      console.log('✅ GM Conversation Analytics: Clearing error');
      state.error = null;
    }
  },

  actions: {
    async fetchConversationData({ commit }, filters = {}) {
      try {
        console.log('🔄 GM Conversation Analytics: Fetching conversation data...');
        commit('SET_LOADING', true);
        commit('CLEAR_ERROR');

        const queryParams = new URLSearchParams(filters);
        const response = await fetch(`/api/v1/accounts/gm_analytics/conversation_analytics?${queryParams}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log('✅ GM Conversation Analytics: Conversation data fetched successfully:', result);

        if (result.success) {
          commit('SET_CONVERSATION_DATA', result.data);
        } else {
          throw new Error(result.error || 'Failed to fetch conversation data');
        }

        return result;
      } catch (error) {
        console.error('❌ GM Conversation Analytics: Error fetching conversation data:', error);
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchVolumeData({ commit }, filters = {}) {
      try {
        console.log('📊 GM Conversation Analytics: Fetching volume data...');
        commit('SET_LOADING', true);
        commit('CLEAR_ERROR');

        const queryParams = new URLSearchParams({ ...filters, type: 'volume' });
        const response = await fetch(`/api/v1/accounts/gm_analytics/conversation_analytics?${queryParams}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log('✅ GM Conversation Analytics: Volume data fetched successfully:', result);

        if (result.success) {
          commit('SET_VOLUME_DATA', result.data);
        } else {
          throw new Error(result.error || 'Failed to fetch volume data');
        }

        return result;
      } catch (error) {
        console.error('❌ GM Conversation Analytics: Error fetching volume data:', error);
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchChannelData({ commit }, filters = {}) {
      try {
        console.log('📊 GM Conversation Analytics: Fetching channel data...');
        commit('SET_LOADING', true);
        commit('CLEAR_ERROR');

        const queryParams = new URLSearchParams({ ...filters, type: 'channels' });
        const response = await fetch(`/api/v1/accounts/gm_analytics/conversation_analytics?${queryParams}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log('✅ GM Conversation Analytics: Channel data fetched successfully:', result);

        if (result.success) {
          commit('SET_CHANNEL_DATA', result.data);
        } else {
          throw new Error(result.error || 'Failed to fetch channel data');
        }

        return result;
      } catch (error) {
        console.error('❌ GM Conversation Analytics: Error fetching channel data:', error);
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchPeakHoursData({ commit }, filters = {}) {
      try {
        console.log('📊 GM Conversation Analytics: Fetching peak hours data...');
        commit('SET_LOADING', true);
        commit('CLEAR_ERROR');

        const queryParams = new URLSearchParams({ ...filters, type: 'peak_hours' });
        const response = await fetch(`/api/v1/accounts/gm_analytics/conversation_analytics?${queryParams}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log('✅ GM Conversation Analytics: Peak hours data fetched successfully:', result);

        if (result.success) {
          commit('SET_PEAK_HOURS_DATA', result.data);
        } else {
          throw new Error(result.error || 'Failed to fetch peak hours data');
        }

        return result;
      } catch (error) {
        console.error('❌ GM Conversation Analytics: Error fetching peak hours data:', error);
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    }
  },

  getters: {
    conversationData: (state) => {
      console.log('📊 GM Conversation Analytics: Getting conversation data:', state.conversationData);
      return state.conversationData;
    },

    volumeData: (state) => {
      console.log('📊 GM Conversation Analytics: Getting volume data:', state.volumeData);
      return state.volumeData;
    },

    channelData: (state) => {
      console.log('📊 GM Conversation Analytics: Getting channel data:', state.channelData);
      return state.channelData;
    },

    peakHoursData: (state) => {
      console.log('📊 GM Conversation Analytics: Getting peak hours data:', state.peakHoursData);
      return state.peakHoursData;
    },

    isLoading: (state) => {
      console.log('📊 GM Conversation Analytics: Getting loading state:', state.isLoading);
      return state.isLoading;
    },

    error: (state) => {
      console.log('📊 GM Conversation Analytics: Getting error:', state.error);
      return state.error;
    },

    hasData: (state) => {
      const hasData = Object.keys(state.conversationData).length > 0;
      console.log('📊 GM Conversation Analytics: Has data:', hasData);
      return hasData;
    }
  }
};

console.log('✅ GM Conversation Analytics Store: Module created successfully');

export default conversationAnalytics;
